import { useFormik } from 'formik';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import * as yup from 'yup';

import { ImagesApp } from '../../../constants/images';
import { useEventContext } from '../../../hooks/contexts/event.context';
import { useI18nContext } from '../../../hooks/contexts/i18n.context';
import { useModalContext } from '../../../hooks/contexts/modal.context';
import WrapperMessage from '../WrapperMessage';
import * as Styles from './styles.section';

export type SectionDiscountsAndPassKeyProps = {
  title: string;
  content: string;
  opened: boolean;
  idRef: number;
  type: 'coupon' | 'passkey' | 'o-globo';
};

const initialValues = { value : '' };

const SectionDiscountsAndPassKey: FC<SectionDiscountsAndPassKeyProps> = ({ title, content, opened, type, idRef }) => {
  const [showContent, setShowContent] = useState(opened);

  const contentContainerRef = useRef<HTMLDivElement>(null);

  const { translate } = useI18nContext();
  const {
    applyPassKey,
    passKeyApplied,
    applyCoupon,
    removeCouponApplied,
    removePassKeyApplied,
    removeSignatureOGloboApplied,
    couponApplied,
    loading,
    applyGloboSignature,
    signatureOGloboApplied,
  } = useEventContext();

  const { modalIsOpen, setContentModal, toggleModal } = useModalContext();

  const isCoupon = type === 'coupon';
  const isPassKey = type === 'passkey';
  const isOGlobo = type === 'o-globo';
  const showWrapperMessage = (
    couponApplied &&
    ((
      isCoupon && !signatureOGloboApplied
    ) || (
      isOGlobo && signatureOGloboApplied
    ))
    && couponApplied.coupon.limit_ticket > 0
  );

  const toggleContent = () => {
    setShowContent(!showContent);
  };

  const getAppliedValue = () => {
    if (isCoupon && !signatureOGloboApplied) return couponApplied?.coupon.name;
    if (isPassKey) return passKeyApplied;
    return signatureOGloboApplied;
  };

  const validationSchema = yup.object<{ value: string }>({
    value : yup.string()
      .required(translate(['INFORMATION', 'REQUIRED_FIELD']))
      .test(
        'cpf-globo',
        translate(['INFORMATION', 'INVALID_COUPON']),
        (value) => {
          if (isCoupon) return !(value === 'CPFGLOBO');
          return true;
        },
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize : true,
    onSubmit : async ({ value }) => {
      if (type === 'passkey') {
        const isValid = await applyPassKey({ passkey : value, modalIsOpen, setContentModal, toggleModal });
        if (!isValid) {
          formik.setErrors({ value : translate(['INFORMATION', 'INVALID_PASSKEY']) });
        }
        return;
      }

      if (type === 'o-globo') {
        const isValid = await applyGloboSignature({
          globoSignature : value,
          modalIsOpen,
          setContentModal,
          toggleModal,
        });
        if (!isValid) {
          formik.setErrors({ value : translate(['INFORMATION', 'INVALID_GLOBO_SIGNATURE']) });
        }
        return;
      }

      const isValid = await applyCoupon({ coupon : value, modalIsOpen, setContentModal, toggleModal });
      if (!isValid) {
        formik.setErrors({ value : translate(['INFORMATION', 'INVALID_COUPON']) });
      }
    },
  });

  useEffect(() => {
    if (!showContent) {
      formik.resetForm();
    }
  }, [
    showContent,
    contentContainerRef,
    couponApplied,
    signatureOGloboApplied,
    passKeyApplied,
    loading
  ]);

  const containsError = formik.errors.value && formik.touched.value;

  useEffect(() => {
    if (couponApplied && isCoupon && !signatureOGloboApplied) {
      setShowContent(true);
      formik.setValues({ value : couponApplied.coupon.name });
    }
  }, [couponApplied]);

  useEffect(() => {
    if (passKeyApplied && isPassKey) {
      setShowContent(true);
      formik.setValues({ value : passKeyApplied });
    }
  }, [passKeyApplied]);

  useEffect(() => {
    if (signatureOGloboApplied && isOGlobo) {
      setShowContent(true);
      formik.setValues({ value : signatureOGloboApplied });
    }
  }, [signatureOGloboApplied]);

  return (
    <Styles.Wrapper $showContent={showContent} $position={idRef} id={`section-discounts-passkey_${idRef}`}>
      <Styles.Header id={`header_${idRef}`}>
        <Styles.Title id={`section-title_${idRef}`}>{title}</Styles.Title>
        <Styles.WrapperToggleShowInput id={`wrapper-toggle-show-input_${idRef}`}>
          <Fragment>
            {(
              couponApplied && isCoupon
            ) || (
              passKeyApplied && isPassKey
            ) || (
              signatureOGloboApplied && isOGlobo
            ) ? (
                <Fragment>
                  <Styles.AppliedItem id={`applied-item_${idRef}`}>
                    {getAppliedValue()}
                  </Styles.AppliedItem>

                  <Styles.ExpandButton
                    onClick={async () => {
                      if (isCoupon && couponApplied) removeCouponApplied();
                      if (isPassKey && passKeyApplied) removePassKeyApplied();
                      if (isOGlobo && signatureOGloboApplied) removeSignatureOGloboApplied();
                      formik.resetForm();
                      toggleContent();
                    }}
                    id={`expand-button-remove_${idRef}`}
                  >
                    <img src={ImagesApp.svg.minus} alt="" />
                  </Styles.ExpandButton>
                </Fragment>
              ) : (
                <Styles.ExpandButton onClick={toggleContent} id={`expand-button-toggle_${idRef}`}>
                  {showContent
                    ? <img src={ImagesApp.svg.close} alt="" />
                    : <img src={ImagesApp.svg.more} alt="" />
                  }
                </Styles.ExpandButton>
              )}
          </Fragment>
        </Styles.WrapperToggleShowInput>
      </Styles.Header>
      <Styles.Content
        ref={contentContainerRef}
        id={`content_${idRef}`}
      >
        {showWrapperMessage && (
          <WrapperMessage idRef={idRef} couponApplied={couponApplied} />
        )}
        <Styles.TextContent id={`text-content_${idRef}`}>{content}</Styles.TextContent>
        {showContent && (
          <Styles.Form
            onSubmit={formik.handleSubmit}
            autoComplete="off"
            $showContent={showContent}
            id={`form_${idRef}`}
          >
            {!(couponApplied || passKeyApplied || signatureOGloboApplied) && (
              <Styles.InputForm
                id={`input-form-value_${idRef}`}
                name="value"
                value={formik.values.value}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="text"
                disabled={Boolean(loading || couponApplied || passKeyApplied || signatureOGloboApplied)}
                placeholder={`${translate(['INFORMATION', 'ENTER_YOUR'])} ${title.toLowerCase()}`}
              />
            )}
            {!(couponApplied || passKeyApplied || signatureOGloboApplied) && (
              <Styles.ButtonForm
                type="submit"
                disabled={Boolean(loading || couponApplied || passKeyApplied || signatureOGloboApplied)}
                id={`button-form-submit_${idRef}`}
              >
                {translate(['INFORMATION', 'TO_APPLY'])}
              </Styles.ButtonForm>
            )}
          </Styles.Form>
        )}
        {containsError && (
          <Styles.ErrorText id={`error-text_${idRef}`}>
            {formik.errors.value}
          </Styles.ErrorText>
        )}
        {!(couponApplied || passKeyApplied || signatureOGloboApplied) && (<Styles.HorizontalLine />)}
      </Styles.Content>
    </Styles.Wrapper>
  );
};

export default SectionDiscountsAndPassKey;
